import { useState } from "react";
import type { JSX } from "react";
import {
  buildExternalAiBulkRecipePrompt,
  buildExternalAiRecipePrompt,
  parseUrlList
} from "../../shared/external-ai-recipe-prompt";
import type { RecipeInput } from "../../shared/recipes";
import { formatTagPath } from "../../shared/recipe-tags";
import {
  createRecipesBulk,
  importRecipeFromUrl,
  importRecipeJson,
  importRecipeJsonBulk,
  parseRecipeDraft,
  parseRecipePhotos
} from "../api/recipes";
import { RecipeForm } from "./RecipeForm";

type RecipeImportProps = {
  onNavigate: (path: string) => void;
};

type ImportMode = "text" | "url" | "photo" | "json" | "bulk";

export function RecipeImport({ onNavigate }: RecipeImportProps): JSX.Element {
  const [mode, setMode] = useState<ImportMode>("text");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [jsonText, setJsonText] = useState("");
  const [bulkJsonText, setBulkJsonText] = useState("");
  const [bulkUrlList, setBulkUrlList] = useState("");
  const [externalAiUrl, setExternalAiUrl] = useState("");
  const [promptCopied, setPromptCopied] = useState(false);
  const [bulkPromptCopied, setBulkPromptCopied] = useState(false);
  const [draft, setDraft] = useState<RecipeInput | undefined>(undefined);
  const [bulkDrafts, setBulkDrafts] = useState<RecipeInput[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [parsing, setParsing] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);

  if (draft !== undefined) {
    return (
      <RecipeForm
        initialInput={draft}
        heading="Review imported recipe"
        onNavigate={onNavigate}
        onCancel={() => { setDraft(undefined); }}
      />
    );
  }

  if (bulkDrafts !== undefined) {
    return (
      <div className="recipes-panel recipe-import">
        <header className="recipes-header">
          <div>
            <p className="eyebrow">Recipes</p>
            <h2>Review bulk import</h2>
            <p className="muted-text">{bulkDrafts.length} recipe{bulkDrafts.length === 1 ? "" : "s"} ready to save.</p>
          </div>
          <button className="secondary-action" type="button" onClick={() => { setBulkDrafts(undefined); }}>
            Back
          </button>
        </header>

        <ul className="bulk-import-list">
          {bulkDrafts.map((recipe, index) => (
            <li key={`${recipe.title}-${String(index)}`} className="bulk-import-item">
              <span className="bulk-import-title">{recipe.title}</span>
              <span className="muted-text">
                {recipe.steps.length} steps · Serves {recipe.servings}
              </span>
              {(recipe.tags ?? []).length > 0 ? (
                <span className="recipe-list-tags">
                  {(recipe.tags ?? []).map((tag) => (
                    <span key={formatTagPath(tag.path)} className="recipe-tag-chip readonly">
                      {formatTagPath(tag.path)}
                    </span>
                  ))}
                </span>
              ) : null}
            </li>
          ))}
        </ul>

        {error !== undefined ? <p className="error-text" role="alert">{error}</p> : null}

        <button
          className="primary-action"
          type="button"
          disabled={savingBulk}
          onClick={() => { void onSaveBulk(); }}
        >
          {savingBulk ? "Saving…" : `Save all ${String(bulkDrafts.length)} recipes`}
        </button>
      </div>
    );
  }

  async function onSaveBulk(): Promise<void> {
    if (bulkDrafts === undefined) {
      return;
    }
    setSavingBulk(true);
    setError(undefined);
    try {
      await createRecipesBulk(bulkDrafts);
      onNavigate("/recipes");
    } catch {
      setError("Could not save all recipes. Check the data and try again.");
      setSavingBulk(false);
    }
  }

  async function onParse(): Promise<void> {
    setParsing(true);
    setError(undefined);
    try {
      if (mode === "bulk") {
        const drafts = await importRecipeJsonBulk(bulkJsonText);
        setBulkDrafts(drafts);
      } else {
        const parsed =
          mode === "text"
            ? await parseRecipeDraft(text)
            : mode === "url"
              ? await importRecipeFromUrl(url)
              : mode === "photo"
                ? await parseRecipePhotos(photos)
                : await importRecipeJson(jsonText);
        setDraft(parsed);
      }
    } catch {
      setError(
        mode === "text"
          ? "Could not parse recipe. Check the text and try again."
          : mode === "url"
            ? "Could not import from URL. Check the link and try again."
            : mode === "photo"
              ? "Could not read the photos. Try clearer images or paste the text instead."
              : mode === "bulk"
                ? "Could not import bulk JSON. Check the array format and try again."
                : "Could not import JSON. Check the format and try again."
      );
    } finally {
      setParsing(false);
    }
  }

  async function onCopyExternalAiPrompt(): Promise<void> {
    setError(undefined);
    try {
      await navigator.clipboard.writeText(buildExternalAiRecipePrompt(externalAiUrl));
      setPromptCopied(true);
      window.setTimeout(() => {
        setPromptCopied(false);
      }, 2000);
    } catch {
      setError("Could not copy the prompt. Check clipboard permissions.");
    }
  }

  async function onCopyBulkExternalAiPrompt(): Promise<void> {
    setError(undefined);
    try {
      await navigator.clipboard.writeText(buildExternalAiBulkRecipePrompt(parseUrlList(bulkUrlList)));
      setBulkPromptCopied(true);
      window.setTimeout(() => {
        setBulkPromptCopied(false);
      }, 2000);
    } catch {
      setError("Could not copy the prompt. Check clipboard permissions.");
    }
  }

  const canSubmit =
    mode === "text"
      ? text.trim().length > 0
      : mode === "url"
        ? url.trim().length > 0
        : mode === "photo"
          ? photos.length > 0
          : mode === "bulk"
            ? bulkJsonText.trim().length > 0
            : jsonText.trim().length > 0;

  return (
    <div className="recipes-panel recipe-import">
      <header className="recipes-header">
        <div>
          <p className="eyebrow">Recipes</p>
          <h2>Import recipe</h2>
        </div>
        <button className="secondary-action" type="button" onClick={() => { onNavigate("/recipes"); }}>
          Cancel
        </button>
      </header>

      <div className="import-mode-toggle" role="tablist" aria-label="Import mode">
        {(["text", "url", "photo", "json", "bulk"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={mode === tab}
            className={mode === tab ? "import-mode active" : "import-mode"}
            onClick={() => { setMode(tab); }}
          >
            {tab === "text"
              ? "Paste text"
              : tab === "url"
                ? "From URL"
                : tab === "photo"
                  ? "From photo"
                  : tab === "json"
                    ? "JSON"
                    : "Bulk"}
          </button>
        ))}
      </div>

      <p className="muted-text">
        {mode === "text"
          ? "Paste recipe prose. The parser extracts structured steps, ingredients, timers, and tags for review."
          : mode === "url"
            ? "Paste a recipe page URL. The app fetches the page and parses it into structured data for review."
            : mode === "photo"
              ? "Upload recipe book pages or screenshots. Text is extracted and parsed into structured data for review."
              : mode === "json"
                ? "Paste canonical recipe JSON for validation and review, or use an external AI agent to generate it."
                : "Paste a JSON array of recipes, or use an external AI agent with multiple URLs to generate bulk import JSON."}
      </p>

      {mode === "text" ? (
        <label className="field">
          <span className="field-label">Recipe text</span>
          <textarea
            className="text-input recipe-import-text"
            rows={14}
            value={text}
            placeholder={"Title on first line\ntag: Desserts > Cakes\ningredient: one onion, thinly sliced\nstep: Bake for 25 minutes"}
            onChange={(event) => { setText(event.target.value); }}
          />
        </label>
      ) : mode === "url" ? (
        <label className="field">
          <span className="field-label">Recipe URL</span>
          <input
            className="text-input"
            type="url"
            value={url}
            placeholder="https://example.com/recipe"
            onChange={(event) => { setUrl(event.target.value); }}
          />
        </label>
      ) : mode === "photo" ? (
        <label className="field">
          <span className="field-label">Recipe photos</span>
          <input
            className="text-input"
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => {
              setPhotos(event.target.files === null ? [] : [...event.target.files]);
            }}
          />
          {photos.length > 0 ? (
            <p className="muted-text">{photos.length} image{photos.length === 1 ? "" : "s"} selected</p>
          ) : null}
        </label>
      ) : mode === "json" ? (
        <>
          <section className="import-json-external" aria-labelledby="external-ai-heading">
            <h3 id="external-ai-heading" className="import-subsection-title">External AI agent</h3>
            <p className="muted-text">
              Copy a prompt for another AI tool. It should fetch your recipe URL and return only JSON matching the Cairn schema (including tags).
            </p>
            <label className="field">
              <span className="field-label">Recipe URL for the agent</span>
              <input
                className="text-input"
                type="url"
                value={externalAiUrl}
                placeholder="https://example.com/recipe"
                onChange={(event) => { setExternalAiUrl(event.target.value); }}
              />
            </label>
            <button className="secondary-action" type="button" onClick={() => { void onCopyExternalAiPrompt(); }}>
              {promptCopied ? "Prompt copied" : "Copy prompt for external AI"}
            </button>
          </section>

          <label className="field">
            <span className="field-label">Recipe JSON</span>
            <textarea
              className="text-input recipe-import-text"
              rows={14}
              value={jsonText}
              placeholder={'{\n  "title": "Example",\n  "servings": 4,\n  "equipment": [],\n  "ingredients": [],\n  "steps": [],\n  "tags": []\n}'}
              onChange={(event) => { setJsonText(event.target.value); }}
            />
          </label>
        </>
      ) : (
        <>
          <section className="import-json-external" aria-labelledby="bulk-external-ai-heading">
            <h3 id="bulk-external-ai-heading" className="import-subsection-title">External AI agent (bulk)</h3>
            <p className="muted-text">
              Paste recipe URLs (comma, space, tab, or newline separated). Copy the prompt for an AI agent to fetch each URL and return only a JSON array for bulk import.
            </p>
            <label className="field">
              <span className="field-label">Recipe URLs</span>
              <textarea
                className="text-input recipe-import-text"
                rows={5}
                value={bulkUrlList}
                placeholder={"https://example.com/recipe-one\nhttps://example.com/recipe-two"}
                onChange={(event) => { setBulkUrlList(event.target.value); }}
              />
            </label>
            <button className="secondary-action" type="button" onClick={() => { void onCopyBulkExternalAiPrompt(); }}>
              {bulkPromptCopied ? "Prompt copied" : "Copy bulk prompt for external AI"}
            </button>
          </section>

          <label className="field">
            <span className="field-label">Bulk recipe JSON array</span>
            <textarea
              className="text-input recipe-import-text"
              rows={14}
              value={bulkJsonText}
              placeholder={'[\n  {\n    "title": "Example",\n    "servings": 4,\n    "equipment": [],\n    "ingredients": [],\n    "steps": [],\n    "tags": []\n  }\n]'}
              onChange={(event) => { setBulkJsonText(event.target.value); }}
            />
          </label>
        </>
      )}

      {error !== undefined ? <p className="error-text" role="alert">{error}</p> : null}

      <button
        className="primary-action"
        type="button"
        disabled={parsing || !canSubmit}
        onClick={() => { void onParse(); }}
      >
        {parsing
          ? "Importing…"
          : mode === "text"
            ? "Parse recipe"
            : mode === "url"
              ? "Import from URL"
              : mode === "photo"
                ? "Import from photos"
                : mode === "bulk"
                  ? "Import bulk JSON"
                  : "Import JSON"}
      </button>
    </div>
  );
}
